import {Typography} from "@material-tailwind/react";
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import React, {useState} from "react";
import {
  BsCheck,
  BsBank,
  BsCashStack,
  BsClock,
  BsCopy,
  BsCreditCard,
  BsGlobe2,
  BsHourglassSplit,
  BsInbox,
  BsPerson,
  BsThreeDots,
  BsX,
} from "react-icons/bs";
import persianNumberFormat from "../../../helper/PersianNumberFormat";
import "./payment-table.css";


const TABLE_HEAD = [
  "شناسه",
  "کاربر",
  "عامل",
  "مبلغ",
  "نوع تراکنش",
  "روش پرداخت",
  "مرجع",
  "وضعیت",
  "زمان",
];

const HEAD_MAP = {
  "شناسه": "id",
  "کاربر": "name",
  "عامل": "sender_person_name",
  "مبلغ": "amount",
  "نوع تراکنش": "payment_origin",
  "روش پرداخت": "payment_method",
  "مرجع": "reference_code",
  "وضعیت": "status",
  "زمان": "time",
};

const PAYMENT_ORIGIN = {
  direct: {title: "پرداخت مستقیم", className: "is-direct"},
  admin_link: {title: "لینک ارسالی", className: "is-admin-link"},
  manual: {title: "ثبت دستی", className: "is-manual"},
};

const PAYMENT_METHOD = {
  payping: {title: "درگاه پرداخت", icon: BsGlobe2, className: "is-gateway"},
  asan: {title: "آسان‌پرداخت", icon: BsBank, className: "is-gateway"},
  asanpardakht: {title: "آسان‌پرداخت", icon: BsBank, className: "is-gateway"},
  card_to_card: {title: "کارت به کارت", icon: BsCreditCard, className: "is-card"},
  cash: {title: "نقدی", icon: BsCashStack, className: "is-cash"},
  other: {title: "سایر", icon: BsThreeDots, className: "is-other"},
};

const PAYMENT_METHOD_ALIASES = {
  "درگاه پرداخت": "payping",
  "آسان‌پرداخت": "asan",
  "آسان پرداخت": "asan",
  "کارت به کارت": "card_to_card",
  "نقدی": "cash",
  "سایر": "other",
};

const STATUS = {
  "در حال پرداخت": {
    className: "is-pending",
    icon: BsHourglassSplit,
  },
  "پرداخت موفق": {
    className: "is-success",
    icon: BsCheck,
  },
  "پرداخت ناموفق": {
    className: "is-failure",
    icon: BsX,
  },
};

function PaymentMethodBadge({method}) {
  const value = method?.trim();
  if (!value) return <span className="payment-empty-value">—</span>;
  const code = value.replace(/ payment method$/, "");
  const meta = PAYMENT_METHOD[PAYMENT_METHOD_ALIASES[code] || code] || {
    title: value, icon: BsThreeDots, className: "is-other",
  };
  const Icon = meta.icon;

  return <span className={`payment-method ${meta.className}`}>
    <span className="payment-method-icon"><Icon aria-hidden="true"/></span>
    <span>{meta.title}</span>
  </span>;
}

function PaymentActor({name, origin}) {
  if ((origin !== "admin_link" && origin !== "manual") || !name?.trim()) {
    return <span className="payment-empty-value">—</span>;
  }
  return <span className="payment-actor-name">
    <BsPerson className="h-3.5 w-3.5" aria-hidden="true"/>
    {name.trim()}
  </span>;
}

function displayReference(referenceCode, manualReference) {
  return referenceCode?.trim() || manualReference?.trim() || "";
}

function splitDateTime(time) {
  if (!time?.trim()) {
    return {date: "", clock: ""};
  }

  const [date = "", clock = ""] = time.trim().split(/\s+/, 2);
  return {date, clock};
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function SortIcon({active, ascending}) {
  if (!active) {
    return <ChevronUpDownIcon className="h-3.5 w-3.5" strokeWidth={2}/>;
  }

  return ascending
    ? <ChevronUpIcon className="h-3.5 w-3.5" strokeWidth={2.2}/>
    : <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={2.2}/>;
}

function StatusBadge({status}) {
  const meta = STATUS[status] || STATUS["پرداخت ناموفق"];
  const Icon = meta.icon;

  return (
    <span className={`payment-status-badge ${meta.className}`}>
      <Icon className="h-3.5 w-3.5"/>
      {status || "نامشخص"}
    </span>
  );
}

function OriginBadge({origin}) {
  const meta = PAYMENT_ORIGIN[origin] || PAYMENT_ORIGIN.direct;

  return (
    <span className={`payment-origin-badge ${meta.className}`}>
      {meta.title}
    </span>
  );
}

function ReferenceCopyButton({reference}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyText(reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className={`payment-reference ${copied ? "is-copied" : ""}`}
      onClick={handleCopy}
      title={copied ? "کپی شد" : "برای کپی کلیک کنید"}
      aria-label={copied ? "مرجع کپی شد" : `کپی مرجع ${reference}`}
    >
      <span className="payment-reference-value" dir="ltr" aria-live="polite">
        {copied ? "کپی شد" : persianNumberFormat(reference)}
      </span>
      {copied
        ? <BsCheck className="payment-reference-icon"/>
        : <BsCopy className="payment-reference-icon"/>}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="payment-table-empty">
      <span className="payment-table-empty-icon">
        <BsInbox className="h-6 w-6"/>
      </span>
      <Typography className="payment-table-empty-title">تراکنشی یافت نشد</Typography>
      <Typography className="payment-table-empty-description">
        فیلترها را تغییر دهید یا آن‌ها را پاک کنید.
      </Typography>
    </div>
  );
}

export function PaymentReportGrid({paymentReport, setSortBy, sortType, setSortType}) {
  const [selectedHead, setSelectedHead] = useState("شناسه");

  const handleSort = (head) => {
    const field = HEAD_MAP[head];
    if (!field) {
      return;
    }

    if (head === selectedHead) {
      setSortType(!sortType);
      return;
    }

    setSelectedHead(head);
    setSortBy(field);
    setSortType(false);
  };

  const handleMobileSortField = (event) => {
    const head = event.target.value;
    setSelectedHead(head);
    setSortBy(HEAD_MAP[head]);
    setSortType(false);
  };

  if (paymentReport.length === 0) {
    return <EmptyState/>;
  }

  return (
    <>
      <div className="payment-mobile-sort flex lg:hidden">
        <label htmlFor="payment-mobile-sort-field">مرتب‌سازی بر اساس</label>
        <div className="flex min-w-0 flex-1 gap-2">
          <select
            id="payment-mobile-sort-field"
            value={selectedHead}
            onChange={handleMobileSortField}
            className="payment-mobile-sort-select"
          >
            {TABLE_HEAD.map((head) => (
              <option value={head} key={head}>{head}</option>
            ))}
          </select>
          <button
            type="button"
            className="payment-mobile-sort-direction"
            onClick={() => setSortType(!sortType)}
            aria-label={sortType ? "مرتب‌سازی نزولی" : "مرتب‌سازی صعودی"}
          >
            <SortIcon active ascending={sortType}/>
          </button>
        </div>
      </div>

      <div className="payment-mobile-list flex lg:hidden">
        {paymentReport.map((transaction) => {
          const {
            id,
            name,
            sender_person_name,
            phone,
            amount,
            payment_origin,
            payment_method,
            reference_code,
            manual_reference,
            status,
            time,
          } = transaction;
          const reference = displayReference(reference_code, manual_reference);
          const {date, clock} = splitDateTime(time);

          return (
            <article className="payment-mobile-card" key={id}>
              <div className="payment-mobile-card-header">
                <div className="min-w-0">
                  <div className="payment-user-name">{name || "بدون نام"}</div>
                  <div className="payment-user-phone">{persianNumberFormat(phone) || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="payment-amount">{persianNumberFormat(amount, true)}</span>
                  <StatusBadge status={status}/>
                </div>
              </div>

              <div className="payment-mobile-card-grid">
                <div>
                  <span>نوع تراکنش</span>
                  <OriginBadge origin={payment_origin}/>
                </div>
                <div>
                  <span>روش پرداخت</span>
                  <PaymentMethodBadge method={payment_method}/>
                </div>
                <div>
                  <span>عامل</span>
                  <PaymentActor name={sender_person_name} origin={payment_origin}/>
                </div>
                <div>
                  <span>مرجع</span>
                  {reference ? (
                    <ReferenceCopyButton reference={reference}/>
                  ) : (
                    <strong>—</strong>
                  )}
                </div>
              </div>

              <div className="payment-mobile-card-footer">
                <span>شناسه #{persianNumberFormat(id, true)}</span>
                <span className="payment-mobile-time inline-flex items-center gap-1.5">
                  <BsClock className="h-3.5 w-3.5"/>
                  {date ? persianNumberFormat(date) : "—"}
                  {clock && `، ${persianNumberFormat(clock)}`}
                </span>
              </div>
            </article>
          );
        })}
      </div>

      <div className="payment-table-shell hidden lg:block">
        <table className="payment-report-table">
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => {
                const active = head === selectedHead;
                return (
                  <th key={head} aria-sort={active ? (sortType ? "ascending" : "descending") : "none"}>
                    <button
                      type="button"
                      className={active ? "is-active" : ""}
                      onClick={() => handleSort(head)}
                    >
                      <span>{head}</span>
                      <SortIcon active={active} ascending={sortType}/>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {paymentReport.map((transaction) => {
              const {
                id,
                name,
                sender_person_name,
                phone,
                amount,
                payment_origin,
                payment_method,
                reference_code,
                manual_reference,
                status,
                time,
              } = transaction;
              const reference = displayReference(reference_code, manual_reference);
              const {date, clock} = splitDateTime(time);

              return (
                <tr key={id}>
                  <td>
                    <span className="payment-transaction-id">#{persianNumberFormat(id, true)}</span>
                  </td>

                  <td>
                    <div className="payment-user-cell">
                      <div className="min-w-0">
                        <div className="payment-user-name">{name || "بدون نام"}</div>
                        <div className="payment-user-phone">{persianNumberFormat(phone) || "—"}</div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <PaymentActor name={sender_person_name} origin={payment_origin}/>
                  </td>

                  <td>
                    <span className="payment-amount">{persianNumberFormat(amount, true)}</span>
                  </td>

                  <td><OriginBadge origin={payment_origin}/></td>

                  <td>
                    <PaymentMethodBadge method={payment_method}/>
                  </td>

                  <td>
                    {reference ? (
                      <ReferenceCopyButton reference={reference}/>
                    ) : (
                      <span className="payment-empty-value">—</span>
                    )}
                  </td>

                  <td><StatusBadge status={status}/></td>

                  <td>
                    <div className="payment-time">
                      <BsClock className="payment-time-icon"/>
                      <span>{date ? persianNumberFormat(date) : "—"}</span>
                      {clock && <small>{persianNumberFormat(clock)}</small>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
